import { useMemo, useRef, useState } from "react";
import { ChevronsUp, ChevronUp, ChevronDown, ChevronsDown, ReceiptText, Star, Pencil } from "lucide-react";
import { favItems, favPages } from "@/lib/favorites";
import FavEditor from "@/components/pos/FavEditor";
import { useApp } from "@/context/AppContext";
import { categoriesFor } from "@/lib/catalog";
import { fmtNum, round2, vals } from "@/lib/format";
import { Box, TKey, TKeypad } from "@/components/terminal/TKeys";

function Ticket({ tk, listRef }) {
  const { money } = useApp();
  return (
    <div className="flex-1 min-h-[220px] flex flex-col bg-white border border-[#C9D3E0] min-w-0">
      <div ref={listRef} className="flex-1 overflow-y-auto" data-testid="tm-ticket-list">
        {tk.cart.map((l) => (
          <button key={l.product_id} type="button" data-pid={l.product_id} onClick={() => tk.setSel(l.product_id)} data-testid="tm-ticket-line"
            className={`w-full text-start px-2 py-1.5 border-b border-[#E3E9F1] text-[13px] ${l.product_id === tk.sel ? "bg-[#FBDDA8]" : "hover:bg-[#F3F7FC]"}`}>
            <span className="flex justify-between gap-2 font-bold"><span className="truncate"><span dir="ltr">{fmtNum(l.qty)}{l.ppk ? " kg" : ""}</span> × {l.name}</span><span dir="ltr">{fmtNum(round2(l.price * l.qty))}</span></span>
            {l.note && <span className="block text-xs italic text-[#6B7A8C]" data-testid="tm-ticket-note">{l.note}</span>}
          </button>
        ))}
      </div>
      <div className="flex justify-between px-2 py-1.5 bg-[#EEF3F9] border-t border-[#C9D3E0] font-extrabold text-lg"><span>Σ</span><span data-testid="tm-ticket-total">{money(tk.totals.total)}</span></div>
    </div>
  );
}

export default function SaleScreen({ tk, buf, q, onClearQ, onKey, act }) {
  const { t, view, money, can } = useApp();
  const pages = favPages(view);
  const [cat, setCat] = useState(() => (pages[0] ? `fav:${pages[0].id}` : "all"));
  const [editing, setEditing] = useState(false);
  const listRef = useRef(null);
  const favPage = !q && cat.startsWith("fav:") ? pages.find((p) => `fav:${p.id}` === cat) : null;
  const products = useMemo(() => (favPage ? favItems(view, favPage) : vals(view.products)
    .filter((p) => !p.deleted && (q ? `${p.name} ${p.barcode || ""}`.toLowerCase().includes(q.toLowerCase()) : cat === "all" || p.category === cat))
    .sort((a, b) => a.name.localeCompare(b.name)).map((p) => ({ p }))), [view, cat, q, favPage]);
  const customer = tk.customerId ? view.customers[tk.customerId] : null;
  const line = tk.cart.find((l) => l.product_id === tk.sel);
  const msg = [customer?.name, line?.name].filter(Boolean).join(" · ") || t("tm_choose");
  const move = (d) => {
    if (!tk.cart.length) return;
    const i = Math.max(0, tk.cart.findIndex((l) => l.product_id === tk.sel));
    const n = d === "first" ? 0 : d === "last" ? tk.cart.length - 1 : Math.min(Math.max(i + d, 0), tk.cart.length - 1);
    tk.setSel(tk.cart[n].product_id);
    listRef.current?.querySelector(`[data-pid="${tk.cart[n].product_id}"]`)?.scrollIntoView({ block: "nearest" });
  };
  const pickCat = (k) => { setCat(k); onClearQ(); };
  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_344px] gap-1 lg:h-full" data-testid="tm-sale-screen">
      <div className="flex flex-col gap-1 min-w-0 lg:min-h-0">
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-1">
          <TKey onClick={act.menu} testid="tm-sale-menu">{t("tm_menu")}</TKey>
          <TKey onClick={act.customer} testid="tm-sale-customer">{t("tm_customer_id")}</TKey>
          <TKey onClick={act.park} testid="tm-sale-account">{t("tm_account")}</TKey>
          <TKey onClick={act.lastTicket} testid="tm-sale-last-ticket">{t("tm_last_ticket")}</TKey>
          <TKey onClick={act.proforma} testid="tm-sale-proforma">{t("tm_proforma")}</TKey>
          <TKey onClick={act.display} testid="tm-sale-display">{t("customer_display")}</TKey>
        </div>
        <div className="grid grid-cols-[minmax(0,1fr)_100px_100px_56px] gap-1">
          <Box className="truncate h-9" testid="tm-sale-message">{msg}</Box>
          <Box className="h-9" testid="tm-sale-tot">{t("tm_tot")} <span dir="ltr">{fmtNum(tk.totals.total)}</span></Box>
          <Box className="h-9" testid="tm-sale-pdt">{t("tm_pending")} <span dir="ltr">{fmtNum(tk.totals.total)}</span></Box>
          <Box className="h-9 text-end text-base" testid="tm-sale-buffer"><span dir="ltr">{buf || "1"}</span></Box>
        </div>
        <div className="flex-1 flex gap-1 min-h-[300px] lg:min-h-0">
          <div className="w-[118px] sm:w-[236px] shrink-0 grid grid-cols-1 sm:grid-cols-2 auto-rows-[86px] gap-1 content-start overflow-y-auto">
            {pages.map((pg) => (
              <TKey key={pg.id} tone="white" active={!q && cat === `fav:${pg.id}`} onClick={() => pickCat(`fav:${pg.id}`)} testid="tm-fav-page">
                <span className="flex flex-col items-center gap-0.5"><Star className="w-4 h-4 text-[#E3A008]" fill="#E3A008" />{pg.name}</span>
              </TKey>
            ))}
            {can("inventory") && <TKey tone="white" onClick={() => setEditing(true)} testid="tm-fav-edit"><span className="flex flex-col items-center gap-0.5"><Pencil className="w-4 h-4" />{t("fav_edit")}</span></TKey>}
            <TKey active={!q && cat === "all"} onClick={() => pickCat("all")} testid="tm-cat-all">{t("all")}</TKey>
            {categoriesFor(view.shop?.type).map(({ key }) => <TKey key={key} active={!q && cat === key} onClick={() => pickCat(key)} testid={`tm-cat-${key}`}>{t(`cat_${key}`)}</TKey>)}
          </div>
          <div className="flex-1 min-w-0 grid grid-cols-[repeat(auto-fill,minmax(112px,1fr))] auto-rows-[86px] gap-1 content-start overflow-y-auto" data-testid="tm-products">
            {q && <TKey tone="white" onClick={onClearQ} className="!text-[#C0392B]" testid="tm-search-chip">✕ “{q}”</TKey>}
            {favPage && products.length === 0 && <div className="col-span-full p-4 text-sm text-[#6B7A8C]" data-testid="tm-fav-empty">{t("fav_empty")}</div>}
            {products.map(({ p, color }) => (
              <TKey key={p.id} tone="peach" style={color ? { background: color } : undefined} onClick={() => act.product(p)} testid="tm-product-btn">
                <span className="line-clamp-3">{p.name}<span className="block font-normal" dir="ltr">{fmtNum(p.price)}{p.sold_by_weight ? "/kg" : ""}</span></span>
              </TKey>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-6 sm:grid-cols-[repeat(2,90px)_1fr_repeat(4,90px)] gap-1">
          <TKey onClick={act.accounts} testid="tm-sale-accounts">{t("tm_accounts")} ({tk.held.length})</TKey>
          <TKey onClick={() => act.pay("advanced")} testid="tm-pay-advanced">{t("tm_advanced")}</TKey>
          <span className="hidden sm:block" />
          <TKey tone="yellow" onClick={() => act.pay("cash")} testid="tm-pay-cash">{t("tm_cash")}</TKey>
          <TKey tone="yellow" onClick={() => act.pay("card")} testid="tm-pay-card">{t("tm_card")}</TKey>
          <TKey tone="yellow" onClick={() => act.pay("split")} testid="tm-pay-split">{t("tm_split")}</TKey>
          <TKey tone="yellow" onClick={() => act.pay("credit")} testid="tm-pay-credit">{t("tm_credit")}</TKey>
        </div>
      </div>
      <div className="flex flex-col gap-1 lg:min-h-0">
        <div className="grid grid-cols-2 gap-1">
          <TKey onClick={act.article} testid="tm-sale-article">{t("tm_article")}</TKey>
          <TKey onClick={act.drawer} testid="tm-sale-drawer">{t("open_drawer")}</TKey>
        </div>
        <div className="flex-1 flex gap-1 lg:min-h-0">
          <div className="w-[80px] grid grid-rows-5 gap-1">
            <TKey tone="white" onClick={() => move("first")} testid="tm-nav-first"><ChevronsUp className="w-8 h-8 text-[#3A6EA5]" /></TKey>
            <TKey tone="white" onClick={() => move(-1)} testid="tm-nav-up"><ChevronUp className="w-8 h-8 text-[#3A6EA5]" /></TKey>
            <div className="grid place-items-center text-[#3A6EA5] text-xs font-bold" data-testid="tm-line-count"><ReceiptText className="w-6 h-6" />{tk.cart.length}</div>
            <TKey tone="white" onClick={() => move(1)} testid="tm-nav-down"><ChevronDown className="w-8 h-8 text-[#3A6EA5]" /></TKey>
            <TKey tone="white" onClick={() => move("last")} testid="tm-nav-last"><ChevronsDown className="w-8 h-8 text-[#3A6EA5]" /></TKey>
          </div>
          <Ticket tk={tk} listRef={listRef} />
        </div>
        <TKeypad onKey={onKey} labels={{ clear: t("tm_clear"), enter: t("tm_enter") }} />
        <div className="grid grid-cols-4 gap-1">
          <TKey onClick={act.note} testid="tm-line-note">{t("tm_line_note")}</TKey>
          <TKey onClick={act.linePrice} testid="tm-line-price">{t("tm_line_price")}</TKey>
          <TKey onClick={act.voidLine} testid="tm-line-void">{t("tm_void_line")}</TKey>
          <TKey onClick={act.reduce} testid="tm-line-reduce">{t("tm_reduce")}</TKey>
        </div>
        <span className="sr-only" data-testid="tm-sale-total-money">{money(tk.totals.total)}</span>
        <FavEditor open={editing} onClose={() => setEditing(false)} />
      </div>
    </div>
  );
}
